import * as React from 'react';
import { observer } from 'mobx-react-lite';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { FluidOwaSource, isFluidEnabledForSource } from 'owa-fluid-validations';
import { Icon } from '@fluentui/react/lib/Icon';
import { OwaFluidPerfLoggingHelper } from 'owa-fluid-perf-datapoints';
import {
    CollabSpace,
    CollabSpaceInlineFeedback,
    createCollabSpaceViewStateId,
    isEventFromDifferentMailbox,
} from 'owa-calendar-collaborative-space';
import { showExistingCollabSpaceForDelegate } from '../utils/showExistingCollabSpaceForDelegate';
import getComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getComposeViewState';
import { ControlIcons } from 'owa-control-icons';
import {
    lazyIsPreloadFluidInCollabSpaceComplete,
    lazyPreloadFluidInCollabSpace,
} from 'owa-fluid-integrations';

const styles = require('./FullComposeLeftPane.scss');
const classNames = require('classnames/bind').bind(styles);

export interface CollabSpaceComposeRowProps {
    event: CalendarEvent;
}

export default observer(function CollabSpaceComposeRow(props: CollabSpaceComposeRowProps) {
    const { event } = props;

    const callFluidPreload = React.useCallback(async () => {
        const isPreloadComplete = await lazyIsPreloadFluidInCollabSpaceComplete.importAndExecute();
        if (isFluidEnabledForSource(FluidOwaSource.CalendarCompose) && !isPreloadComplete) {
            lazyPreloadFluidInCollabSpace.importAndExecute();
        }
    }, []);

    React.useEffect(() => {
        callFluidPreload();

        if (!getComposeViewState().collabSpaceViewStateId) {
            createCollabSpaceViewStateId();
        }
    }, [event]);

    // We disable the collab space entirely if its an external account or a group calendar
    // For delegates we only show the collab space if it already exists on the event and is enabled.
    if (
        !isFluidEnabledForSource(FluidOwaSource.CalendarCompose) ||
        (isEventFromDifferentMailbox(event) && !showExistingCollabSpaceForDelegate()) ||
        !getComposeViewState().collabSpaceViewStateId
    ) {
        return null;
    }

    const viewStateId = getComposeViewState().collabSpaceViewStateId;

    return (
        <div className={classNames(styles.row, styles.collabSpaceComposeRow)}>
            <OwaFluidPerfLoggingHelper fluidOwaSource={FluidOwaSource.CalendarCompose} />
            <Icon
                iconName={ControlIcons.OneNoteLogo}
                className={classNames(styles.descriptionIcon, styles.collabSpaceIconStyle)}
            />
            <div className={styles.collabSpaceContainer}>
                <CollabSpace
                    event={event}
                    fluidOwaSource={FluidOwaSource.CalendarCompose}
                    viewStateId={viewStateId}
                />
                <CollabSpaceInlineFeedback viewStateId={viewStateId} />
            </div>
        </div>
    );
});
